import { useCallback, useMemo } from "react";
import { capitalize, sortBy } from "lodash";
import { useAppContext } from "~/contexts/appContext";
import { useGetArenaAccessQuery } from "~/graphql/supergraphHooks";
import { type ArenaAccessFilterInput } from "~/graphql/supergraphTypes";
import {
  formatFullDateAndTimeWithLocalTimezoneV2,
  formatShortFormDate,
} from "~/utils/datetime";
import { ArenaAccessRow } from "./components/WorkspaceTable";

type Args = { globalUserId?: string; active?: boolean };

export const useFetchWorkspaceRowData = (args?: Args) => {
  const { globalUserId, active } = args || {};
  const { user } = useAppContext();
  const filters: ArenaAccessFilterInput = {};

  if (globalUserId) {
    filters.userId = globalUserId;
  }

  if (active !== undefined) {
    filters.active = active;
  }

  const response = useGetArenaAccessQuery({
    variables: {
      filters,
    },

    fetchPolicy: "network-only",
  });

  const formatDateString = useCallback(
    (dateString: string | undefined, formatFunction: (d: string) => string) => {
      return typeof dateString === "string" ? formatFunction(dateString) : "--";
    },
    [],
  );

  const formatTimezone = useCallback(
    (s: string) => formatFullDateAndTimeWithLocalTimezoneV2(s, "", true),
    [],
  );

  const rowData: ArenaAccessRow[] = useMemo(() => {
    const formattedData =
      response.data?.arenaAccess?.output?.map((o) => ({
        id: o.id,
        arenaName: o.arenaName,
        arenaId: o.arenaId,
        arenaDomain: `${o.arenaDomain}.junipersquare.com`,
        roleGroupName: o.roleGroupName,
        active: o.active,
        accessType: capitalize(o.accessType),
        accessVia: o.isBulkAccess ? "All arena" : "Direct",
        endDate: formatDateString(o.endDate ?? undefined, formatShortFormDate),
        lastAccess: formatDateString(o.lastAccessTimestamp ?? undefined, formatTimezone),
        login: {
          arenaId: o.arenaId,
          adminUserId: user.id,
          active: o.active,
        },
      })) || [];
    return sortBy(formattedData, ["arenaName"]);
  }, [response.data?.arenaAccess?.output, formatDateString, formatTimezone, user.id]);
  return { response, rowData };
};
